import type { SubscriptionDetails } from "schemas/generated/billing";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { CancelPlanModal } from "@v2/features/billing/modals/CancelPlanModal";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useIsWorkspaceOwner } from "@v2/hooks/useIsWorkspaceOwner";
import { CircleEllipsis, CircleX } from "lucide-react";

import { getStripePortalUrl } from "../shared/sharedBillingUtils";

const ManagePlanMenu = ({
  workspaceId,
  activeSubscription,
}: {
  workspaceId: string;
  activeSubscription: SubscriptionDetails;
}) => {
  const isWorkspaceOwner = useIsWorkspaceOwner(workspaceId);
  const [cancelPlanModalOpen, setCancelPlanModalOpen] = React.useState(false);

  const handleCancelPlan = React.useCallback(() => {
    if (!isWorkspaceOwner) {
      return;
    }

    analytics.logEvent("billing.cancelModal.opened", {
      currentPlan: activeSubscription.billingTier,
    });

    setCancelPlanModalOpen(true);
  }, [isWorkspaceOwner, activeSubscription.billingTier]);

  let cancelPlanTooltipText: string | undefined;
  if (!isWorkspaceOwner) {
    cancelPlanTooltipText =
      "Only Workspace Owners have the ability to cancel plans";
  } else if (activeSubscription.isManagedByReplo) {
    cancelPlanTooltipText =
      "Your plan is managed by Replo. To cancel, please contact Replo support at billing@replo.app";
  }

  const isCancelPlanEnabled =
    isWorkspaceOwner && !activeSubscription.isManagedByReplo;

  const isSubscriptionAlreadyCanceled =
    activeSubscription.endedAt && activeSubscription.endedAt > new Date();

  const isStripeSubscription = activeSubscription.paymentProcessor === "stripe";

  const hasMenuItems = isStripeSubscription || !isSubscriptionAlreadyCanceled;

  if (!hasMenuItems) {
    return null;
  }

  const cancelPlanMenuItem = (
    <MenuItem
      label="Cancel Plan"
      variant="destructive"
      disabled={!isCancelPlanEnabled}
      startEnhancer={<CircleX />}
      onClick={handleCancelPlan}
    />
  );

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Button variant="secondary" size="sm">
              Manage Payments
            </Button>
          }
        />
        <MenuContent align="start">
          {isStripeSubscription && (
            <MenuItem
              label="Edit Payment Details"
              startEnhancer={<CircleEllipsis />}
              onClick={() => {
                window.open(getStripePortalUrl(workspaceId), "_blank");
              }}
            />
          )}
          {!isSubscriptionAlreadyCanceled &&
            (cancelPlanTooltipText ? (
              <Tooltip content={cancelPlanTooltipText} triggerAsChild>
                {cancelPlanMenuItem}
              </Tooltip>
            ) : (
              cancelPlanMenuItem
            ))}
        </MenuContent>
      </Menu>
      <CancelPlanModal
        open={cancelPlanModalOpen}
        onOpenChange={setCancelPlanModalOpen}
        source="direct.billingDashboard"
      />
    </>
  );
};

export default ManagePlanMenu;
